import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2, Loader2, FileText } from "lucide-react";
import { formatUGX } from "@/lib/api";
import jsPDF from "jspdf";

export default function StoryCard({ property, trigger }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !property) return;
    setReady(false);
    setLoading(true);

    const W = 1080, H = 1920; // Instagram Story / WhatsApp Status 9:16
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    const draw = (img) => {
      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#991b1b");
      bg.addColorStop(1, "#450a0a");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Photo panel (top 55%)
      const ph = Math.round(H * 0.58);
      // Cover-fit photo into rect (40, 40, W-80, ph-40)
      const px = 40, py = 40, pw = W - 80, phh = ph - 40;
      const iw = img.width, ih = img.height;
      const scale = Math.max(pw / iw, phh / ih);
      const dw = iw * scale, dh = ih * scale;
      const dx = px + (pw - dw) / 2;
      const dy = py + (phh - dh) / 2;
      // Rounded rect clip
      roundedClip(ctx, px, py, pw, phh, 40);
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.restore();

      // Gold badge - Featured
      ctx.fillStyle = "#eab308";
      roundedRect(ctx, px + 30, py + 30, 170, 60, 30);
      ctx.fillStyle = "#991b1b";
      ctx.font = "700 26px 'Manrope', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("TIGAWA", px + 30 + 85, py + 30 + 30);

      // Title status pill
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      const titleStr = (property.title_status || "").toUpperCase();
      ctx.font = "700 22px 'Manrope', sans-serif";
      const tw = ctx.measureText(titleStr).width + 40;
      roundedRect(ctx, W - px - 30 - tw, py + 30, tw, 60, 30);
      ctx.fillStyle = "#991b1b";
      ctx.fillText(titleStr, W - px - 30 - tw / 2, py + 30 + 30);

      // Bottom content area
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      const cy = ph + 60;

      // Eyebrow
      ctx.fillStyle = "#eab308";
      ctx.font = "800 26px 'Manrope', sans-serif";
      ctx.fillText("TIGAWA PROPERTIES · KAMPALA | NYC | BOSTON | COLUMBUS", 60, cy);

      // Title (wrap up to 3 lines)
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 74px 'Fraunces', Georgia, serif";
      const titleLines = wrapText(ctx, property.title, W - 120, 3);
      titleLines.forEach((ln, i) => ctx.fillText(ln, 60, cy + 50 + i * 88));

      // Location
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "500 32px 'Manrope', sans-serif";
      const locY = cy + 50 + titleLines.length * 88 + 20;
      const locStr = `📍 ${property.location} · ${property.district}`;
      ctx.fillText(clip(ctx, locStr, W - 120), 60, locY);

      // Price big
      ctx.fillStyle = "#eab308";
      ctx.font = "800 100px 'Fraunces', Georgia, serif";
      const priceY = locY + 90;
      ctx.fillText(formatUGX(property.price_ugx), 60, priceY);

      // Size
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.font = "600 30px 'Manrope', sans-serif";
      ctx.fillText(`${property.size} ${property.size_unit} · ${property.property_type}`, 60, priceY + 130);

      // Footer bar
      const fy = H - 180;
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.fillRect(0, fy, W, 180);
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 34px 'Manrope', sans-serif";
      ctx.fillText("Call · +256 700 259 675 · +256 701 238 066", 60, fy + 40);
      ctx.fillText("WhatsApp · +1 614 571 9333", 60, fy + 90);
      ctx.fillStyle = "#eab308";
      ctx.font = "600 24px 'Manrope', sans-serif";
      ctx.fillText("www.tigawaproperties.com", 60, fy + 138);

      setLoading(false);
      setReady(true);
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => draw(img);
    img.onerror = () => {
      // fallback: no photo, still render text card
      const placeholder = document.createElement("canvas");
      placeholder.width = 4; placeholder.height = 4;
      const p2 = placeholder.getContext("2d");
      p2.fillStyle = "#991b1b"; p2.fillRect(0, 0, 4, 4);
      const img2 = new Image();
      img2.onload = () => draw(img2);
      img2.src = placeholder.toDataURL();
    };
    // Use direct URL — must be same-origin (uploads/) or CORS-enabled
    img.src = property.images?.[0] || "";
  }, [open, property]);

  const download = () => {
    const link = document.createElement("a");
    link.download = `tigawa-${(property?.title || "listing").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  const downloadPDF = () => {
    const canvas = canvasRef.current;
    // A4 portrait: 210 x 297 mm. Our canvas is 1080x1920 (9:16) — fit width, center vertically.
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgAR = 1080 / 1920;
    const w = pageW - 20;
    const h = w / imgAR;
    const x = 10;
    const y = Math.max(10, (pageH - h) / 2);
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", x, y, w, h);
    pdf.save(`tigawa-flyer-${(property?.title || "listing").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`);
  };

  const shareWhatsApp = async () => {
    const blob = await new Promise((r) => canvasRef.current.toBlob(r, "image/png"));
    const file = new File([blob], "tigawa-story.png", { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: property.title,
          text: `${property.title} — ${formatUGX(property.price_ugx)}\n${property.location}, ${property.district}\nvia TIGAWA PROPERTIES`,
        });
        return;
      } catch { /* user cancelled */ }
    }
    const text = encodeURIComponent(`${property.title} — ${formatUGX(property.price_ugx)}\n${property.location}, ${property.district}\nvia TIGAWA PROPERTIES\n+256 700 259 675`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display text-xl">Share this listing</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-4 pt-2">
          <div className="relative aspect-[9/16] w-full rounded-xl overflow-hidden bg-secondary" data-testid="story-card-preview">
            {loading && (
              <div className="absolute inset-0 grid place-items-center bg-secondary/80 text-secondary-foreground">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
            <canvas ref={canvasRef} className="w-full h-full object-contain" />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <Button onClick={download} disabled={!ready} className="rounded-full bg-primary hover:bg-primary/90" data-testid="story-download-btn">
              <Download className="w-4 h-4" /> PNG
            </Button>
            <Button onClick={downloadPDF} disabled={!ready} variant="outline" className="rounded-full border-border" data-testid="story-pdf-btn">
              <FileText className="w-4 h-4" /> PDF
            </Button>
            <Button onClick={shareWhatsApp} disabled={!ready} variant="outline" className="rounded-full border-border" data-testid="story-share-btn">
              <Share2 className="w-4 h-4" /> Share
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
            1080×1920 · perfect for Instagram Story, WhatsApp Status, or as an A4 broadcast flyer PDF.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helpers
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
}
function roundedClip(ctx, x, y, w, h, r) {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.clip();
}
function wrapText(ctx, text, maxWidth, maxLines) {
  const words = text.split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? cur + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
      if (lines.length === maxLines - 1) break;
    } else {
      cur = test;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines) {
    // add ellipsis if truncated
    while (ctx.measureText(lines[maxLines - 1] + "…").width > maxWidth) {
      lines[maxLines - 1] = lines[maxLines - 1].slice(0, -1);
    }
  }
  return lines;
}
function clip(ctx, str, maxWidth) {
  if (ctx.measureText(str).width <= maxWidth) return str;
  while (ctx.measureText(str + "…").width > maxWidth && str.length) str = str.slice(0, -1);
  return str + "…";
}
